import React, {
  useMemo,
  useContext,
  createContext,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { observer } from "mobx-react";
import { parseAbsolute, now } from "@internationalized/date";
import { useParams } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Popover from "@mui/material/Popover";

import DatePicker from "shared/AriaDatePicker";
import className from "shared/className";
import Loader from "shared/Loader";
import config from "../../../../config";
import DockedList, { SearchFeild } from "../DockedList";
import "./EventsSidebar.scss";
import { MonitorContext } from "../../Monitor";
import EventsStore, { severities } from "./EventsStore";
import { severityIcons } from "../../images";
import { useStore } from "../../../../AppStore";
import { SidebarContext } from "../Sidebar";
import EventFiltersStore from "./EventFiltersStore";
import StickyList, { findListTime } from "./StickyList";

const EventsContext = createContext();

export default observer(function EventsSidebar({ parent }) {
  const monitor = useContext(MonitorContext);
  const monitorStore = monitor.store;
  const appStore = useStore();
  const routeParams = useParams();
  const stickyList = useRef();
  const eventsStore = useMemo(() => {
    const store = new EventsStore({
      monitorStore,
      appStore,
    });

    let { timestamp, historyType } = routeParams;
    let eventTime;
    timestamp = historyType?.match(/event/) && timestamp;
    store.loadingEvents = true;
    if (timestamp) {
      eventTime = decodeURIComponent(timestamp);
      store.fetchPmEvents({ eventTime });
    } else {
      eventTime = undefined;
      // liveMode triggers fetch
      store.liveMode = true;
    }
    return store;
  }, []);
  useEffect(() => () => eventsStore.destroy(), []);

  return (
    <EventsContext.Provider value={eventsStore}>
      <DockedList
        className="events"
        query={eventsStore.query}
        searchControls={<SearchControls stickyList={stickyList} />}
      >
        <div className="events-side-bar">
          {!eventsStore.eventsObj && eventsStore.loadingEvents ? (
            <Loader />
          ) : (
            <StickyList
              eventsStore={eventsStore}
              parent={parent}
              ref={stickyList}
            />
          )}
        </div>
      </DockedList>
    </EventsContext.Provider>
  );
});

const EventFilters = observer(function EventFilters({ stickyList }) {
  const eventsStore = useContext(EventsContext);
  const width = useContext(SidebarContext).width;
  const store = useMemo(() => new EventFiltersStore(eventsStore), []);

  const range = {
    start: parseAbsolute(eventsStore.minDateTime.toISO(), config.timezone), //.toFormat('yyyy-MM-dd'),
    end: now(config.timezone),
  };

  const placeholderValue = useMemo(
    () =>
      parseAbsolute(store?.time || new Date().toISOString(), config.timezone),
    [],
  );
  const timeChange = useCallback((absDate) => {
    let time = absDate?.toDate().toISOString();
    if (time?.match(/invalid/i)) time = undefined;
    store.time = time;
  });
  const onSubmit = async (e) => {
    e.preventDefault();
    eventsStore.showFilters = false;
    await eventsStore.submit(store);
    if (store.time) {
      stickyList.current.dispatchEvent(
        new CustomEvent("timeSet", {
          detail: { timestamp: store.time, behavior: "instant" },
        }),
      );
    }
  };
  return (
    <div className="event-filters" style={{ minWidth: width }}>
      <form onSubmit={onSubmit}>
        <SearchFeild
          query={store.query}
          search={(query) => (store.query = query)}
        />
        <div className="actions">
          <button type="submit" className="btn">
            Submit
          </button>
          <a
            href="cancel"
            aria-label="cancel"
            onClick={(e) => {
              e.preventDefault();
              eventsStore.showFilters = false;
            }}
          >
            Cancel
          </a>
        </div>
        <Severities {...{ store, severities }} />
        <DatePicker
          dateField={{
            minValue: range.start,
            maxValue: range.end,
            hourCycle: 24,
            granularity: "second",
            defaultValue: placeholderValue,
            "aria-label": "Select Alarm at date/time",
            onChange: timeChange,
          }}
        />
      </form>
    </div>
  );
});

const Severities = observer(({ severities, store }) => (
  <div className="severities">
    {severities.map((severity) => (
      <SeverityButton key={severity} severity={severity} store={store} />
    ))}
  </div>
));

const SeverityButton = observer(function SeverityButton({ store, severity }) {
  return (
    <button
      {...className({
        selected: store.selectedSeverities.has(severity),
        deselected:
          store.selectedSeverities.size &&
          !store.selectedSeverities.has(severity),
        [`${severity}-severity`]: 1,
      })}
      onClick={(e) => {
        e.preventDefault();
        store.toggleSeverity({ severity });
      }}
    >
      <img src={severityIcons[severity]} alt={severity} />
      {severity}
    </button>
  );
});

const SearchControls = observer(({ stickyList }) => {
  const store = useContext(EventsContext);
  const id = "event-filters";
  const filterButton = useRef();

  const openFilters = useCallback(async () => {
    await new Promise((r) => requestAnimationFrame(r));
    const list = stickyList.current;
    if (!list) return;
    store.time = store.liveMode ? undefined : findListTime(list);
    store.showFilters = true;
  });

  return (
    <>
      <button
        aria-describedby={id}
        ref={filterButton}
        className="btn open-filters"
        aria-label="open filters"
        onClick={openFilters}
      >
        <FilterAltIcon /> Filters <FilterCount />
      </button>
      <Popover
        id={id}
        open={store.showFilters}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        anchorEl={store.showFilters ? filterButton.current : null}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={() => (store.showFilters = false)}
      >
        <EventFilters stickyList={stickyList} />
      </Popover>
      <button
        {...className("btn live-toggle", { live: store.liveMode })}
        aria-label={
          store.liveMode ? "pause loading events" : "get latest events"
        }
        onClick={() => {
          store.liveMode = !store.liveMode;
        }}
      >
        <RefreshIcon className="refresh-icon" />
      </button>
    </>
  );
});

function FilterCount() {
  const { filterCount } = useContext(EventsContext);
  return filterCount ? <div className="count">{filterCount}</div> : null;
}
